<template>
    <DataTable
        :header="$t('deviationsFromRecommendedPrice')"
        :fields="fields"
        :items="chartsData"
        :loading="request.getLoading()"
        :error="request.getError()"
        :hover="false"
        class="mb-4"
        thead-class="d-none"
        table-class="table-layout-fixed"
        :overflow="false"
    >
        <template #cell(chart)="{item}">
            <EChart :options="item" type="time" />
        </template>
    </DataTable>
</template>



<script>
    import EChart from '@/components/common/EChart.vue';
    import DataTable from '@/components/common/DataTable.vue';

    export default {
        name: 'DeviationsFromRecommendedPriceInTime',
        components: {
            EChart,
            DataTable,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
            };
        },
        computed: {
            fields() {
                return [{ key: 'chart', label: '' }];
            },
            data() {
                return this.parentComponent.data;
            },
            chartsData() {
                if (!this.data || !this.data.products) {
                    return [];
                }

                return Object.keys(this.data.products).map(productKey => {
                    const productData = this.data.products[productKey];
                    const product = productData.product;
                    const priceArray = productData.price;
                    const recommendedPrice = product.price;

                    // Sortujemy dane cenowe wg daty
                    const sortedPrices = [...priceArray].sort((a, b) => new Date(a.date) - new Date(b.date));

                    // Pobieramy listę sklepów dynamicznie (wszystkie klucze poza "date")
                    const storeSet = new Set();
                    sortedPrices.forEach(obj => {
                        Object.keys(obj).forEach(key => {
                            if (key !== 'date') {
                                storeSet.add(key);
                            }
                        });
                    });
                    const stores = Array.from(storeSet).sort();

                    // Dla każdego sklepu generujemy serię – każdy punkt ma wartość równą cenie sklepowej (rawPrice)
                    // oraz dodatkowe informacje wykorzystywane w tooltipie
                    const seriesForStores = stores.map(store => {
                        const data = sortedPrices.map(obj => {
                            const rawPrice = obj[store] !== null && obj[store] !== undefined ? obj[store] : null;
                            return {
                                value: [obj.date, rawPrice],
                                store,
                                rawPrice,
                                deviation: rawPrice !== null && recommendedPrice !== null ? rawPrice - recommendedPrice : null,
                                deviationPct: rawPrice !== null && recommendedPrice ? ((rawPrice - recommendedPrice) / recommendedPrice) * 100 : null,
                                recommendedPrice,
                            };
                        });
                        return {
                            name: store,
                            type: 'line',
                            showSymbol: true,
                            data,
                            areaStyle: null,
                            itemStyle: {
                                opacity: 0,
                            },
                            emphasis: {
                                itemStyle: {
                                    opacity: 1,
                                },
                            },
                        };
                    });

                    // Seria referencyjna – linia na poziomie ceny rekomendowanej
                    const referenceLineSeries = {
                        name: this.$t('recommendedPrice'),
                        type: 'line',
                        smooth: false,
                        data: [
                            [sortedPrices[0].date, recommendedPrice],
                            [sortedPrices[sortedPrices.length - 1].date, recommendedPrice],
                        ],
                        lineStyle: { color: 'black' },
                        symbol: 'none',
                        areaStyle: null,
                    };

                    return {
                        product,
                        grid: {
                            left: '40px',
                            top: '80px',
                        },
                        title: {
                            text: product.name,
                            subtext: `EAN: ${product.ean}`,
                            textStyle: {
                                fontSize: 14,
                            },
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: params => {
                                const d = params.data;
                                if (d && d.rawPrice !== null) {
                                    const isValue = num => num !== null && num !== undefined;
                                    const formatCurrency = num => (isValue(num) ? this.$format.currency(num) : '-');
                                    const formatPercent = num => (isValue(num) ? this.$format.percent(num) : '-');
                                    const priceStr = formatCurrency(d.rawPrice);
                                    const devStr = d.deviation !== null ? `${d.deviation >= 0 ? '+' : ''}${formatCurrency(d.deviation)}` : '-';
                                    const devPctStr = d.deviationPct !== null ? `${d.deviationPct >= 0 ? '+' : ''}${formatPercent(d.deviationPct)}` : '-';
                                    const deviationStr = d.deviation !== null ? `${devPctStr} (${devStr})` : '-';

                                    const tooltipItemsHtml = [
                                        { title: this.$t('price'), value: priceStr },
                                        { title: this.$t('recommendedPrice'), value: formatCurrency(recommendedPrice) },
                                        { title: this.$t('deviation'), value: deviationStr },
                                        { title: this.$t('date'), value: params.value[0] },
                                    ].reduce((acc, item) => `${acc}<div style="display:flex;gap:10px;">${item.title}: <strong style="margin-left:auto">${item.value}</strong></div>`, '');

                                    return `
                                        <div style="display:flex;flex-direction:column;gap:3px;">
                                            <div style="margin-bottom:2px;">${params.marker}<strong>${d.store}</strong></div>
                                            ${tooltipItemsHtml}
                                        </div>
                                    `;
                                }
                                return '';
                            },
                        },
                        xAxis: {
                            type: 'time',
                            boundaryGap: false,
                        },
                        yAxis: {
                            type: 'value',
                            name: this.$t('price'),
                            nameRotate: 90,
                            nameLocation: 'middle',
                            nameGap: 50,
                        },
                        legend: {
                            data: [this.$t('recommendedPrice'), ...stores],
                        },
                        series: [referenceLineSeries, ...seriesForStores],
                    };
                });
            },
        },
    };
</script>
